import { useEffect, useRef } from 'react';

/**
 * Global Easter Egg — on hover of the navbar logo rocket icon,
 * the rocket flies off, tours the screen, and returns with a landing effect.
 * Next launch only possible after current one finishes.
 */
export default function GlobalRocketEasterEgg() {
  const rocketRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>(0);
  const flyingRef = useRef(false);

  useEffect(() => {
    const TRAIL_COUNT = 36;
    type TrailDot = { x: number; y: number; age: number };
    const trail: TrailDot[] = [];

    let rocketX = 0;
    let rocketY = 0;
    let angle = 0;

    type Pt = { x: number; y: number };
    let pathPoints: Pt[] = [];
    let pathT = 0;
    let pathSpeed = 0;

    const cubicBezier = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const mt = 1 - t;
      return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
    };

    const getLogoIconPos = (): Pt => {
      const el = document.getElementById('navbar-rocket-icon');
      if (el) {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }
      return { x: 48, y: 32 };
    };

    const buildPath = (start: Pt): Pt[] => {
      const W = window.innerWidth;
      const H = window.innerHeight;

      const mid1 = { x: W * (0.55 + Math.random() * 0.4),  y: H * (0.04 + Math.random() * 0.2) };
      const mid2 = { x: W * (0.04 + Math.random() * 0.28), y: H * (0.45 + Math.random() * 0.45) };
      const mid3 = { x: W * (0.5  + Math.random() * 0.45), y: H * (0.5  + Math.random() * 0.38) };
      const mid4 = { x: W * (0.08 + Math.random() * 0.35), y: H * (0.04 + Math.random() * 0.2) };
      const end  = getLogoIconPos();

      const pts: Pt[] = [];
      const STEPS = 480;

      for (let i = 0; i <= STEPS; i++) {
        const t = i / STEPS;
        let x: number, y: number;

        if (t < 0.25) {
          const s = t / 0.25;
          x = cubicBezier(start.x, mid1.x, mid1.x, mid2.x, s);
          y = cubicBezier(start.y, mid1.y, mid1.y, mid2.y, s);
        } else if (t < 0.5) {
          const s = (t - 0.25) / 0.25;
          x = cubicBezier(mid2.x, mid2.x, mid3.x, mid3.x, s);
          y = cubicBezier(mid2.y, mid2.y, mid3.y, mid3.y, s);
        } else if (t < 0.75) {
          const s = (t - 0.5) / 0.25;
          x = cubicBezier(mid3.x, mid3.x, mid4.x, mid4.x, s);
          y = cubicBezier(mid3.y, mid3.y, mid4.y, mid4.y, s);
        } else {
          const s = (t - 0.75) / 0.25;
          x = cubicBezier(mid4.x, mid4.x + (end.x - mid4.x) * 0.5, end.x, end.x, s);
          y = cubicBezier(mid4.y, mid4.y + (end.y - mid4.y) * 0.5, end.y - 28, end.y, s);
        }

        pts.push({ x, y });
      }

      return pts;
    };

    const launch = () => {
      if (flyingRef.current) return;
      flyingRef.current = true;

      const start = getLogoIconPos();
      rocketX = start.x;
      rocketY = start.y;
      pathPoints = buildPath(start);
      pathT = 0;
      pathSpeed = 0.0004;

      // Hide the logo icon
      const iconEl = document.getElementById('navbar-rocket-icon');
      if (iconEl) {
        iconEl.style.opacity = '0';
        iconEl.style.transition = 'opacity 0.1s ease';
      }

      const el = rocketRef.current;
      if (el) {
        el.style.left = `${rocketX}px`;
        el.style.top = `${rocketY}px`;
        el.style.opacity = '1';
      }
    };

    const showLandingEffect = (x: number, y: number) => {
      // Restore icon with pop
      const iconEl = document.getElementById('navbar-rocket-icon');
      if (iconEl) {
        iconEl.style.transition = 'opacity 0.12s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1)';
        iconEl.style.opacity = '1';
        iconEl.style.transform = 'scale(1.6)';
        setTimeout(() => { iconEl.style.transform = 'scale(1)'; }, 40);
      }

      // Shockwave rings from landing spot
      const trailEl = trailRef.current;
      if (!trailEl) return;
      for (let r = 0; r < 3; r++) {
        setTimeout(() => {
          const ring = document.createElement('div');
          ring.style.cssText = `
            position:fixed;
            left:${x}px;
            top:${y}px;
            width:14px;
            height:14px;
            border-radius:50%;
            border:2px solid rgba(249,115,22,0.85);
            transform:translate(-50%,-50%) scale(1);
            opacity:0.85;
            pointer-events:none;
            transition:transform 0.55s ease-out, opacity 0.55s ease-out;
            z-index:9999;
          `;
          trailEl.appendChild(ring);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              ring.style.transform = 'translate(-50%,-50%) scale(4.5)';
              ring.style.opacity = '0';
            });
          });
          setTimeout(() => ring.remove(), 620);
        }, r * 130);
      }
    };

    const tick = () => {
      frameRef.current = requestAnimationFrame(tick);
      if (!flyingRef.current) return;

      const el = rocketRef.current;
      if (!el) return;

      // Speed curve: ease in → cruise → ease out
      if (pathT < 0.07) pathSpeed = 0.0003 + pathT * 0.013;
      else if (pathT > 0.9) pathSpeed = Math.max(0.0004, pathSpeed * 0.983);
      else pathSpeed = 0.0021;

      pathT = Math.min(1, pathT + pathSpeed);

      const idx = Math.min(Math.floor(pathT * (pathPoints.length - 1)), pathPoints.length - 1);
      const nextIdx = Math.min(idx + 1, pathPoints.length - 1);
      const pt = pathPoints[idx];
      const nextPt = pathPoints[nextIdx];

      rocketX = pt.x;
      rocketY = pt.y;

      const dx = nextPt.x - pt.x;
      const dy = nextPt.y - pt.y;
      if (Math.abs(dx) + Math.abs(dy) > 0.05) {
        angle = Math.atan2(dy, dx) + Math.PI / 2;
      }

      el.style.left = `${rocketX}px`;
      el.style.top = `${rocketY}px`;
      el.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;

      // Trail
      trail.push({ x: rocketX, y: rocketY, age: 0 });
      if (trail.length > TRAIL_COUNT) trail.shift();
      trail.forEach((d) => { d.age += 1; });

      const trailEl = trailRef.current;
      if (trailEl) {
        trailEl.innerHTML = '';
        trail.forEach((d) => {
          const ratio = 1 - d.age / TRAIL_COUNT;
          const opacity = ratio * 0.88;
          const size = Math.max(1.5, 7 * ratio);
          const fireColors = ['#fde68a', '#fb923c', '#f97316', '#ea580c', '#dc2626'];
          const color = fireColors[Math.floor((1 - ratio) * (fireColors.length - 1))];
          const dot = document.createElement('div');
          dot.style.cssText = `
            position:fixed;
            left:${d.x}px;top:${d.y}px;
            width:${size}px;height:${size}px;
            border-radius:50%;
            background:${color};
            opacity:${opacity};
            transform:translate(-50%,-50%);
            filter:blur(${1.4 * (1 - ratio)}px);
            pointer-events:none;
          `;
          trailEl.appendChild(dot);
        });
      }

      // Landed
      if (pathT >= 1) {
        flyingRef.current = false;
        el.style.opacity = '0';
        trail.length = 0;
        if (trailEl) trailEl.innerHTML = '';
        showLandingEffect(rocketX, rocketY);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    // Attach hover listener to the icon — use event delegation so it works
    // even if the navbar re-renders
    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.id === 'navbar-rocket-icon' || target.closest('#navbar-logo-box')) {
        launch();
      }
    };

    // Mobile: tap on the logo box triggers the easter egg
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.id === 'navbar-rocket-icon' || target.closest('#navbar-logo-box')) {
        e.preventDefault();
        launch();
      }
    };

    document.addEventListener('mouseover', handleMouseEnter);
    document.addEventListener('touchstart', handleTouchStart, { passive: false });

    return () => {
      document.removeEventListener('mouseover', handleMouseEnter);
      document.removeEventListener('touchstart', handleTouchStart);
      cancelAnimationFrame(frameRef.current);
      const iconEl = document.getElementById('navbar-rocket-icon');
      if (iconEl) iconEl.style.opacity = '1';
    };
  }, []);

  return (
    <>
      <div
        ref={trailRef}
        style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9998 }}
      />
      <div
        ref={rocketRef}
        style={{
          position: 'fixed',
          zIndex: 9999,
          opacity: 0,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          color: 'white',
          filter: 'drop-shadow(0 0 5px rgba(249,115,22,1)) drop-shadow(0 0 14px rgba(249,115,22,0.6))',
          transformOrigin: 'center center',
        }}
      >
        <i className="ri-rocket-2-fill" />
      </div>
    </>
  );
}
